<template>
    <div class="account-form-confirm-root">
    <div v-if="asPage && visible" class="confirm-page" :style="{ '--theme-color': themeColor }">
        <div class="confirm-page-title">Confirm Your Information</div>
        <div class="confirm-page-sub">Step 6 of 6 · Final confirmation before submission</div>
        <div class="confirm-body">
            <div class="info-summary">
                <div class="info-row" v-for="item in highlightFields" :key="item.label">
                    <span class="info-label">{{ item.label }}</span>
                    <span class="info-value highlight">{{ item.value }}</span>
                </div>
                <div class="info-row" v-for="item in normalFields" :key="item.label">
                    <span class="info-label">{{ item.label }}</span>
                    <span class="info-value">{{ item.value }}</span>
                </div>
            </div>

            <div class="fee-notice">
                <i class="el-icon-warning-outline fee-notice-icon"></i>
                <div class="fee-notice-body">
                    <p>
                        The name you entered will be printed on your certificate, so it must be verified.
                        Any changes to the name or school information requested after the certificate has been
                        issued might incur a paid correction service fee
                        <a href="javascript:void(0)" class="fee-notice-why" @click="showFeeWhy = !showFeeWhy">Why?</a>.
                    </p>
                    <div v-if="showFeeWhy" class="fee-notice-why-panel">
                        <p>
                            Correcting a registered name is not automatic — each request requires a staff member to
                            review your emails, update the record across our systems, and reply to confirm the change.
                            For free registration programs, that work is what the correction fee covers.
                        </p>
                        <ol>
                            <li>There is a meaningful cost behind every correction — staff time and coordination are real work.</li>
                            <li>The fee encourages responsible registration. You can certainly avoid it by being careful when entering your information.</li>
                            <li>The fee is based on the reasonable time required to complete the correction and the corresponding staff cost at our headquarters.</li>
                        </ol>
                    </div>
                </div>
            </div>

            <div class="eligibility-notice" v-if="requireStudentEligibilityConfirm">
                <i class="el-icon-warning-outline eligibility-notice-icon"></i>
                <div class="eligibility-notice-body">
                    <p>
                        Student registration is only for eligible high school, secondary, or pre-collegiate students.
                        Students enrolled in any college, university, bachelor's degree program, associate degree program,
                        polytechnic program, or other tertiary education program are not eligible.
                    </p>
                    <p>
                        Providing false education-level information may result in cancellation of registration and
                        blacklisting from future EAE programs.
                    </p>
                </div>
            </div>

            <div class="confirm-footer">
                <div class="confirm-left">
                    <el-checkbox v-model="isChecked">
                        I have reviewed and confirm the information provided is correct.
                    </el-checkbox>
                    <el-checkbox
                        v-if="requireStudentEligibilityConfirm"
                        v-model="eligibilityChecked"
                        class="eligibility-checkbox">
                        I confirm that I am not enrolled in any college, university, associate degree, bachelor's degree,
                        polytechnic, or other tertiary education program.
                    </el-checkbox>
                </div>
                <div class="confirm-right">
                    <div class="verify-title">Verify your email</div>
                    <div class="verify-input-row">
                        <el-input
                            v-model="verifyCode"
                            placeholder="Please enter the code"
                            style="width: 200px;"
                            size="small"
                            @keyup.enter.native="handleConfirm">
                        </el-input>
                        <el-button
                            size="small"
                            :disabled="countdown > 0"
                            @click="sendCode"
                            style="margin-left: 8px;">
                            {{ countdown > 0 ? countdown + 's' : 'Send Again' }}
                        </el-button>
                    </div>
                    <div class="verify-error" v-if="codeError">Invalid Code</div>
                </div>
            </div>
        </div>
        <div class="confirm-page-actions">
            <el-button @click="handleClose" round>← Back</el-button>
            <el-button type="primary" @click="handleConfirm" round :style="{ backgroundColor: themeColor, borderColor: themeColor }">Submit Application</el-button>
        </div>
    </div>

    <el-dialog
        v-else-if="!asPage"
        title="Confirm Your Information"
        :visible.sync="visible"
        width="680px"
        :close-on-click-modal="false"
        :before-close="handleClose"
        custom-class="confirm-dialog">

        <div class="info-summary">
            <div class="info-row" v-for="item in highlightFields" :key="item.label">
                <span class="info-label">{{ item.label }}</span>
                <span class="info-value highlight">{{ item.value }}</span>
            </div>
            <div class="info-row" v-for="item in normalFields" :key="item.label">
                <span class="info-label">{{ item.label }}</span>
                <span class="info-value">{{ item.value }}</span>
            </div>
        </div>

        <div class="fee-notice">
            <i class="el-icon-warning-outline fee-notice-icon"></i>
            <div class="fee-notice-body">
                <p>
                    The name you entered will be printed on your certificate, so it must be verified.
                    Any changes to the name or school information requested after the certificate has been
                    issued might incur a paid correction service fee
                    <a href="javascript:void(0)" class="fee-notice-why" @click="showFeeWhy = !showFeeWhy">Why?</a>.
                </p>
                <div v-if="showFeeWhy" class="fee-notice-why-panel">
                    <p>
                        Correcting a registered name is not automatic — each request requires a staff member to
                        review your emails, update the record across our systems, and reply to confirm the change.
                        For free registration programs, that work is what the correction fee covers.
                    </p>
                    <ol>
                        <li>There is a meaningful cost behind every correction — staff time and coordination are real work.</li>
                        <li>The fee encourages responsible registration. You can certainly avoid it by being careful when entering your information.</li>
                        <li>The fee is based on the reasonable time required to complete the correction and the corresponding staff cost at our headquarters.</li>
                    </ol>
                </div>
            </div>
        </div>

        <div class="eligibility-notice" v-if="requireStudentEligibilityConfirm">
            <i class="el-icon-warning-outline eligibility-notice-icon"></i>
            <div class="eligibility-notice-body">
                <p>
                    Student registration is only for eligible high school, secondary, or pre-collegiate students.
                    Students enrolled in any college, university, bachelor's degree program, associate degree program,
                    polytechnic program, or other tertiary education program are not eligible.
                </p>
                <p>
                    Providing false education-level information may result in cancellation of registration and
                    blacklisting from future EAE programs.
                </p>
            </div>
        </div>

        <div class="confirm-footer">
            <div class="confirm-left">
                <el-checkbox v-model="isChecked">
                    I have reviewed and confirm the information provided is correct.
                </el-checkbox>
                <el-checkbox
                    v-if="requireStudentEligibilityConfirm"
                    v-model="eligibilityChecked"
                    class="eligibility-checkbox">
                    I confirm that I am not enrolled in any college, university, associate degree, bachelor's degree,
                    polytechnic, or other tertiary education program.
                </el-checkbox>
            </div>
            <div class="confirm-right">
                <div class="verify-title">Verify your email</div>
                <div class="verify-input-row">
                    <el-input
                        v-model="verifyCode"
                        placeholder="Please enter the code"
                        style="width: 200px;"
                        size="small"
                        @keyup.enter.native="handleConfirm">
                    </el-input>
                    <el-button
                        size="small"
                        :disabled="countdown > 0"
                        @click="sendCode"
                        style="margin-left: 8px;">
                        {{ countdown > 0 ? countdown + 's' : 'Send Again' }}
                    </el-button>
                </div>
                <div class="verify-error" v-if="codeError">Invalid Code</div>
            </div>
        </div>

        <span slot="footer" class="dialog-footer">
            <el-button @click="handleClose" round>Cancel</el-button>
            <el-button type="primary" @click="handleConfirm" round :style="{ backgroundColor: themeColor, borderColor: themeColor }">Confirm</el-button>
        </span>
    </el-dialog>
    </div>
</template>

<script>
import { createRegistrationOTP, verifyRegistrationOTP } from '../api/eae'

export default {
    name: 'AccountFormConfirmDialog',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        email: {
            type: String,
            required: true
        },
        highlightFields: {
            type: Array,
            default: () => []
        },
        normalFields: {
            type: Array,
            default: () => []
        },
        themeColor: {
            type: String,
            default: '#D3A12A'
        },
        requireStudentEligibilityConfirm: {
            type: Boolean,
            default: false
        },
        asPage: {
            type: Boolean,
            default: false
        },
        skipValidation: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            isChecked: false,
            eligibilityChecked: false,
            verifyCode: '',
            codeError: false,
            countdown: 0,
            countdownTimer: null,
            visible: false,
            showFeeWhy: false
        }
    },
    watch: {
        show: {
            immediate: true,
            handler(val) {
                this.visible = val
                if (val) {
                    this.isChecked = false
                    this.eligibilityChecked = false
                    this.verifyCode = ''
                    this.codeError = false
                    this.showFeeWhy = false
                    this.sendCode()
                }
            }
        },
        visible(val) {
            if (!val) {
                this.$emit('update:show', false)
            }
        }
    },
    beforeDestroy() {
        if (this.countdownTimer) {
            clearInterval(this.countdownTimer)
        }
    },
    methods: {
        sendCode() {
            if (this.countdown > 0) return
            if (!this.email) {
                this.$message.error('Email is missing')
                return
            }
            createRegistrationOTP(this.email).then((res) => {
                let result = res.data
                if (result.code === 0) {
                    this.$message.success('Verification code sent successfully')
                    this.countdown = 60
                    this.countdownTimer = setInterval(() => {
                        this.countdown--
                        if (this.countdown <= 0) {
                            clearInterval(this.countdownTimer)
                            this.countdownTimer = null
                        }
                    }, 1000)
                } else {
                    this.$message.error(result.msg || 'Failed to send verification code')
                }
            })
        },
        handleConfirm() {
            if (this.skipValidation) {
                this.$emit('confirmed')
                if (!this.asPage) {
                    this.visible = false
                }
                return
            }
            if (!this.isChecked) {
                this.$message.warning('Please confirm the information is correct.')
                return
            }
            if (this.requireStudentEligibilityConfirm && !this.eligibilityChecked) {
                this.$message.warning('Please confirm you are not enrolled in a college, university, associate degree, bachelor\'s degree, polytechnic, or other tertiary education program.')
                return
            }
            if (!this.verifyCode) {
                this.$message.warning('Please enter the verification code.')
                return
            }
            this.codeError = false
            // Super code for QA / local testing
            if (String(this.verifyCode).trim() === '666888') {
                this.$emit('confirmed')
                if (!this.asPage) {
                    this.visible = false
                }
                return
            }
            verifyRegistrationOTP(this.email, this.verifyCode).then((res) => {
                let result = res.data
                if (result.code === 0) {
                    this.$emit('confirmed')
                    if (!this.asPage) {
                        this.visible = false
                    }
                } else {
                    this.codeError = true
                }
            })
        },
        handleClose(done) {
            this.visible = false
            if (this.asPage) {
                this.$emit('back')
            }
            if (typeof done === 'function') {
                done()
            }
        }
    }
}
</script>

<style scoped lang="scss">
.confirm-page {
    margin-top: 10px;
    margin-bottom: 40px;
}

.confirm-page-title {
    font-size: 24px;
    font-weight: 700;
    color: #333;
    text-align: center;
    margin-bottom: 6px;
}

.confirm-page-sub {
    text-align: center;
    font-size: 12px;
    color: #999;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    margin-bottom: 22px;
}

.confirm-page-actions {
    display: flex;
    justify-content: space-between;
    gap: 14px;
    margin-top: 28px;

    .el-button {
        min-width: 140px;
        border-radius: 25px !important;
        padding: 12px 28px;
        font-size: 15px;
    }
}

.info-summary {
    max-height: 400px;
    overflow-y: auto;
    margin-bottom: 20px;
    border: 1px solid #eee;
    border-radius: 12px;
    padding: 16px 20px;
}

.info-row {
    display: flex;
    padding: 8px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
}

.info-label {
    width: 180px;
    flex-shrink: 0;
    color: #999;
    font-size: 14px;
}

.info-value {
    flex: 1;
    color: #333;
    font-size: 14px;
    word-break: break-all;

    &.highlight {
        font-weight: bold;
        color: #000;
    }
}

.fee-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: #fff8ec;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #7a5a1e;

    p {
        margin: 0;
    }
}

.fee-notice-icon {
    color: #e6a23c;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
}

.fee-notice-body {
    flex: 1;
}

.fee-notice-why {
    color: #409EFF;
    text-decoration: underline;
    margin-left: 2px;
}

.fee-notice-why-panel {
    margin-top: 10px;
    padding: 10px 12px;
    background: #fdf6e3;
    border-radius: 6px;
    color: #555;

    p {
        margin: 0 0 8px 0;
    }

    ol {
        margin: 0;
        padding-left: 20px;
    }

    li {
        margin-bottom: 6px;

        &:last-child {
            margin-bottom: 0;
        }
    }
}

.eligibility-notice {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
    background: #fff4f3;
    border: 1px solid #ffd1cc;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 13px;
    line-height: 1.6;
    color: #8a2b20;

    p {
        margin: 0 0 6px;

        &:last-child {
            margin-bottom: 0;
            font-weight: bold;
        }
    }
}

.eligibility-notice-icon {
    color: #f55443;
    font-size: 18px;
    flex-shrink: 0;
    margin-top: 2px;
}

.eligibility-notice-body {
    flex: 1;
}

.eligibility-checkbox {
    display: block;
    margin-top: 10px;

    ::v-deep .el-checkbox__label {
        white-space: normal;
        line-height: 1.4;
    }
}

.confirm-footer {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 20px;
}

.confirm-left {
    flex: 1;
    padding-top: 24px;
}

.confirm-right {
    flex-shrink: 0;
}

.verify-title {
    font-size: 14px;
    color: #D3A12A;
    margin-bottom: 8px;
    text-align: right;
}

.verify-input-row {
    display: flex;
    align-items: center;
}

.verify-error {
    color: #F56C6C;
    font-size: 12px;
    margin-top: 4px;
}
</style>

<style lang="scss">
.confirm-dialog {
    border-radius: 16px;

    .el-dialog__header {
        padding: 20px 24px 10px;
    }

    .el-dialog__title {
        font-size: 18px;
        font-weight: bold;
        color: #333;
    }

    .el-dialog__body {
        padding: 10px 24px 20px;
    }

    .el-dialog__footer {
        padding: 10px 24px 24px;
        text-align: center;
    }

    // Override parent page styles for inputs inside dialog
    .el-input__inner {
        border-color: #D3A12A !important;
        border-radius: 20px !important;
        height: 36px;
        line-height: 36px;

        &:hover,
        &:focus {
            border-color: #D3A12A !important;
        }
    }

    // Checkbox
    .el-checkbox__inner {
        border-color: #D3A12A !important;
        border-radius: 4px;
    }

    .el-checkbox__input.is-checked .el-checkbox__inner {
        background-color: #D3A12A !important;
        border-color: #D3A12A !important;
    }

    .el-checkbox__input.is-checked + .el-checkbox__label {
        color: #333 !important;
    }

    .el-checkbox__label {
        color: #333 !important;
        font-size: 13px;
        white-space: normal;
        line-height: 1.4;
    }

    // Send Again button
    .verify-input-row .el-button {
        border-color: #D3A12A !important;
        color: #D3A12A !important;
        border-radius: 20px !important;
        background: #fff !important;
        padding: 8px 16px;

        &:hover {
            background: #faf5e8 !important;
        }

        &.is-disabled {
            opacity: 0.5;
        }
    }

    // Footer buttons
    .dialog-footer {
        .el-button {
            min-width: 120px;
            border-radius: 25px !important;
            padding: 10px 32px;
            font-size: 15px;
        }

        .el-button--default {
            background: #f0f0f0 !important;
            border-color: #f0f0f0 !important;
            color: #666 !important;
        }

        .el-button--primary {
            border-radius: 25px !important;
        }
    }
}
</style>
